<template>
  <div class="px-6 py-4">
    <div class="flex items-center justify-between mb-4">
      <button
        type="button"
        class="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        @click="$emit('back')"
      >
        <font-awesome-icon :icon="['fas', 'arrow-left']" class="text-xs" />
        Back to targets
      </button>
      <div class="flex items-center gap-2">
        <button type="button" class="btn-secondary-sm" @click="copyLink">{{ copied ? 'Link copied' : 'Copy link' }}</button>
        <ExportMenu
          v-if="data && data.brief"
          size="lg"
          :exports="[
            { type: 'pdf', action: () => exportBrief('pdf'), loading: exporting === 'pdf' },
            { type: 'docx', action: () => exportBrief('docx'), loading: exporting === 'docx' },
          ]"
        />
      </div>
    </div>

    <div v-if="loading" class="text-sm text-gray-500 py-8 text-center">Loading brief…</div>
    <div v-else-if="!data || !data.brief" class="text-sm text-gray-500 py-8 text-center">
      No brief has been written for this practice yet.
    </div>
    <template v-else>
    <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div class="p-5 border-b border-gray-200">
        <div class="flex justify-between items-start gap-4">
          <div>
            <h2 class="sensitive text-xl font-semibold text-gray-900">{{ data.name }}</h2>
            <div class="text-sm text-gray-600 mt-0.5">
              <span class="sensitive">{{ place }}</span> · {{ data.providers }} providers · {{ fmtInt(data.lives) }} Medicare patients
            </div>
          </div>
          <div class="text-right text-xs text-gray-500 leading-5 shrink-0">
            Brief written {{ writtenOn }}<br />Claims through {{ data.year }}
          </div>
        </div>
        <div class="flex flex-wrap gap-1.5 mt-3">
          <PracticeTag v-if="data.is_target" category="type" variant="success">Target</PracticeTag>
          <PracticeTag v-else category="type">Not a target</PracticeTag>
          <PracticeTag v-for="t in structuralTagLabels" :key="t" category="setting">{{ t }}</PracticeTag>
          <PracticeTag category="aco" :class="data.program ? 'sensitive' : ''">{{ data.program ? data.program : 'No ACO' }}</PracticeTag>
          <PracticeTag v-if="owner" category="type" filled class="sensitive">{{ owner }}</PracticeTag>
          <PracticeTag v-else category="type" filled>Independent</PracticeTag>
          <PracticeTag v-for="t in findingTags" :key="t" category="tech">{{ t }}</PracticeTag>
        </div>
        <p class="sensitive text-sm text-gray-700 mt-3" data-demo-blur>
          <span class="font-semibold" :class="brief.target.is_target ? 'text-secondary-900' : 'text-gray-900'">
            {{ brief.target.is_target ? 'A target.' : 'Not a target.' }}
          </span>
          {{ brief.target.reason }}
        </p>
        <p class="sensitive text-sm text-gray-900 font-medium mt-2" data-demo-blur>{{ brief.headline }}</p>
      </div>

      <div class="p-5 space-y-8">
        <section>
          <h3 class="heading-subsection mb-2">What this practice does, and doesn't do</h3>
          <ul class="divide-y divide-gray-100 border-y border-gray-100">
            <li v-for="(b, i) in brief.what_they_do" :key="i" class="flex gap-3 py-2.5 text-sm text-gray-800">
              <font-awesome-icon
                :icon="['fas', b.does_it ? 'check' : 'xmark']"
                class="mt-1 text-xs shrink-0"
                :class="b.does_it ? 'text-secondary-800' : 'text-gray-400'"
              />
              <span class="sensitive" data-demo-blur>
                <span class="font-medium text-gray-900">{{ b.behavior }}</span>
                <span class="text-gray-700"> {{ b.evidence }}</span>
                <span class="text-[11px] text-gray-400 ml-1">{{ cite(b.facts) }}</span>
              </span>
            </li>
          </ul>
        </section>

        <section>
          <h3 class="heading-subsection mb-1">Where it sits against a good practice and a better one</h3>
          <p class="text-sm text-gray-600 mb-3">
            Both comparisons are real practices in the same cohort
            <span v-if="data.cohort_key">({{ cohortLabel }})</span>.
            <span class="sensitive">{{ brief.peers.good_name }}</span> is doing well.
            <span class="sensitive">{{ brief.peers.better_name }}</span> is one of the best.
          </p>
          <table class="w-full text-sm">
            <thead>
              <tr class="text-xs text-gray-500 border-b border-gray-200">
                <th class="text-left font-medium py-2 pr-3"></th>
                <th class="text-right font-semibold text-gray-900 py-2 px-3 w-28 sensitive">{{ shortName }}</th>
                <th class="text-right font-medium py-2 px-3 w-32">A good practice</th>
                <th class="text-right font-medium py-2 pl-3 w-28">A better one</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="(r, i) in brief.peers.rows" :key="i">
                <td class="py-2 pr-3 text-gray-800">
                  {{ r.metric }}
                  <div class="text-[11px] text-gray-500 sensitive" data-demo-blur>{{ r.meaning }}</div>
                </td>
                <td class="py-2 px-3 text-right tabular-nums font-semibold text-gray-900 bg-gray-50 sensitive">{{ r.practice }}</td>
                <td class="py-2 px-3 text-right tabular-nums text-gray-700">{{ r.good }}</td>
                <td class="py-2 pl-3 text-right tabular-nums text-gray-700">{{ r.better }}</td>
              </tr>
            </tbody>
          </table>
          <div class="mt-3 grid grid-cols-[auto_1fr] gap-4 items-center">
            <MetricCard :value="upsideMid" label="Upside per year" :format="fmtMoney" />
            <p class="sensitive text-sm text-gray-700" data-demo-blur>
              {{ fmtMoney(brief.peers.upside_low) }} to {{ fmtMoney(brief.peers.upside_high) }}. {{ brief.peers.upside_driver }}
            </p>
          </div>
        </section>

        <section>
          <h3 class="heading-subsection mb-2">Can it improve with what you bring</h3>
          <div class="px-4 py-3 rounded-lg border" :class="verdictBox">
            <div class="text-sm font-semibold mb-1">{{ verdictLabel }}</div>
            <p class="sensitive text-sm text-gray-800" data-demo-blur>{{ brief.can_improve.why }}</p>
          </div>
        </section>

        <section>
          <h3 class="heading-subsection mb-2">The three things holding it back</h3>
          <div class="divide-y divide-gray-100 border-y border-gray-100">
            <div v-for="(b, i) in brief.blockers" :key="i" class="py-3">
              <div class="sensitive text-sm font-semibold text-gray-900" data-demo-blur>{{ b.blocker }}</div>
              <p class="sensitive text-sm text-gray-800 mt-1" data-demo-blur>{{ b.evidence }} <span class="text-[11px] text-gray-400">{{ cite(b.facts) }}</span></p>
              <div class="mt-2 inline-flex items-start gap-2 text-xs text-gray-700 bg-gray-50 rounded-md px-2.5 py-1.5">
                <span class="font-semibold text-gray-900 shrink-0">What you bring</span>
                <span>{{ b.what_you_bring }}</span>
              </div>
            </div>
          </div>
        </section>

        <section v-if="brief.aco">
          <h3 class="heading-subsection mb-2">ACO history and shared savings</h3>
          <p class="sensitive text-sm text-gray-800" data-demo-blur>
            <span class="font-semibold text-gray-900">{{ acoStatusLabel }}</span> {{ brief.aco.summary }}
            <span class="text-[11px] text-gray-400 ml-1">{{ cite(brief.aco.facts) }}</span>
          </p>
          <table v-if="programRows.length" class="w-full text-sm mt-3">
            <thead>
              <tr class="text-xs text-gray-500 border-b border-gray-200">
                <th class="text-left font-medium py-2 pr-3 w-14">Year</th>
                <th class="text-left font-medium py-2 px-3">ACO</th>
                <th class="text-left font-medium py-2 px-3 w-28">Program</th>
                <th class="text-right font-medium py-2 px-3 w-28">Lives here</th>
                <th class="text-right font-medium py-2 px-3 w-28">Savings rate</th>
                <th class="text-right font-medium py-2 px-3 w-32">Earned savings</th>
                <th class="text-right font-medium py-2 pl-3 w-20">Quality</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="r in programRows" :key="r.YEAR + r.PROGRAM_ID">
                <td class="py-2 pr-3 tabular-nums text-gray-800">{{ r.YEAR }}</td>
                <td class="py-2 px-3 text-gray-900 sensitive">{{ r.PROGRAM_NAME }}<span v-if="r.TRACK" class="text-gray-500"> · {{ r.TRACK }}</span></td>
                <td class="py-2 px-3 text-gray-700">{{ r.PROGRAM }}</td>
                <td class="py-2 px-3 text-right tabular-nums sensitive">{{ fmtInt(r.LIVES) }}</td>
                <td class="py-2 px-3 text-right tabular-nums">{{ fmtPct(r.SAVINGS_RATE) }}</td>
                <td class="py-2 px-3 text-right tabular-nums">{{ r.EARNED_SAVINGS == null ? '' : fmtMoney(r.EARNED_SAVINGS) }}</td>
                <td class="py-2 pl-3 text-right tabular-nums">{{ fmtPct(r.QUALITY_SCORE) }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="text-sm text-gray-600 mt-2">No ACO participation on record for any year.</p>
          <template v-if="acoResultRows.length">
            <div class="text-xs font-medium text-gray-700 mt-4 mb-1">How {{ acoResultRows[0].PROGRAM_NAME }} has performed</div>
            <table class="w-full text-sm">
              <thead>
                <tr class="text-xs text-gray-500 border-b border-gray-200">
                  <th class="text-left font-medium py-2 pr-3 w-14">Year</th>
                  <th class="text-right font-medium py-2 px-3">Assigned lives</th>
                  <th class="text-right font-medium py-2 px-3">Savings rate</th>
                  <th class="text-right font-medium py-2 px-3">Needed to earn</th>
                  <th class="text-right font-medium py-2 px-3">Vs benchmark</th>
                  <th class="text-right font-medium py-2 px-3">Earned</th>
                  <th class="text-right font-medium py-2 px-3">Quality</th>
                  <th class="text-right font-medium py-2 pl-3">Cost vs benchmark</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="r in acoResultRows" :key="r.PROGRAM_ID + r.YEAR">
                  <td class="py-1.5 pr-3 tabular-nums text-gray-800">{{ r.YEAR }}</td>
                  <td class="py-1.5 px-3 text-right tabular-nums">{{ fmtInt(r.ASSIGNED_LIVES) }}</td>
                  <td class="py-1.5 px-3 text-right tabular-nums" :class="r.SAVINGS_RATE < 0 ? 'text-red-dark' : ''">{{ fmtPct(r.SAVINGS_RATE) }}</td>
                  <td class="py-1.5 px-3 text-right tabular-nums text-gray-500">{{ fmtPct(r.MIN_SAVINGS_RATE) }}</td>
                  <td class="py-1.5 px-3 text-right tabular-nums">{{ r.GENERATED_SAVINGS == null ? '' : fmtMoney(r.GENERATED_SAVINGS) }}</td>
                  <td class="py-1.5 px-3 text-right tabular-nums">{{ r.EARNED_SAVINGS == null ? '' : fmtMoney(r.EARNED_SAVINGS) }}</td>
                  <td class="py-1.5 px-3 text-right tabular-nums">{{ fmtPct(r.QUALITY_SCORE) }}</td>
                  <td class="py-1.5 pl-3 text-right tabular-nums">{{ r.PROGRAM_PMPY == null || r.BENCHMARK_PMPY == null ? '' : '$' + fmtInt(r.PROGRAM_PMPY) + ' vs $' + fmtInt(r.BENCHMARK_PMPY) }}</td>
                </tr>
              </tbody>
            </table>
          </template>
          <p class="sensitive text-sm text-gray-800 mt-3" data-demo-blur>
            <span class="font-semibold text-gray-900">For the meeting.</span> {{ brief.aco.implication }}
          </p>
        </section>

        <section>
          <h3 class="heading-subsection mb-2">The last {{ years.length }} years, read correctly</h3>
          <div class="grid grid-cols-[1fr_360px] gap-6 items-start">
            <div class="space-y-3">
              <p class="sensitive text-sm text-gray-800" data-demo-blur>{{ brief.story.summary }}</p>
              <p v-if="brief.story.structural_break" class="sensitive text-sm text-gray-800" data-demo-blur>
                <span class="font-semibold text-gray-900">Structural break.</span> {{ brief.story.structural_break }}
              </p>
              <ul class="text-sm text-gray-700 space-y-1">
                <li v-for="n in brief.story.year_notes" :key="n.year" class="sensitive" data-demo-blur>
                  <span class="tabular-nums font-medium text-gray-900">{{ n.year }}</span> {{ n.note }}
                </li>
              </ul>
            </div>
            <div v-if="years.length" class="text-[11px] text-gray-600">
              <svg :viewBox="`0 0 ${chartWidth} 200`" class="w-full h-auto" role="img" aria-label="Medicare patients by year">
                <text x="0" y="12" fill="#626264" font-size="13">Medicare patients</text>
                <template v-for="(y, i) in years" :key="y.YEAR">
                  <rect
                    :x="10 + i * colWidth"
                    :y="180 - barHeight(y.LIVES)"
                    :width="colWidth - 22"
                    :height="barHeight(y.LIVES)"
                    :fill="i > breakIndex ? '#4C6E97' : '#C1D6F0'"
                    class="sensitive"
                  />
                  <text
                    :x="10 + i * colWidth + (colWidth - 22) / 2"
                    :y="180 - barHeight(y.LIVES) - 5"
                    text-anchor="middle"
                    fill="#353537"
                    class="sensitive"
                    font-size="12"
                  >{{ fmtInt(y.LIVES) }}</text>
                  <text :x="10 + i * colWidth + (colWidth - 22) / 2" y="196" text-anchor="middle" fill="#626264" font-size="12">{{ y.YEAR }}</text>
                </template>
                <template v-if="breakIndex >= 0">
                  <line :x1="breakX" y1="84" :x2="breakX" y2="184" stroke="#1E1F21" stroke-dasharray="3 3" />
                  <text :x="breakX + 4" y="82" fill="#1E1F21" font-weight="600" font-size="12">Panel changes</text>
                </template>
              </svg>
              <svg v-if="pmpySeries.length > 1" :viewBox="`0 0 ${chartWidth} 96`" class="w-full h-auto mt-2" role="img" aria-label="Cost per patient, adjusted">
                <text x="0" y="12" fill="#626264" font-size="13">Cost per patient, adjusted for how sick they are</text>
                <line x1="20" y1="60" :x2="chartWidth - 18" y2="60" stroke="#E8E9E9" />
                <polyline :points="pmpyPoints" fill="none" stroke="#353537" stroke-width="1.5" />
                <template v-for="p in pmpySeries" :key="p.year">
                  <circle :cx="10 + p.index * colWidth + (colWidth - 22) / 2" :cy="pmpyY(p.value)" r="2.5" fill="#353537" />
                  <text :x="10 + p.index * colWidth + (colWidth - 22) / 2" y="80" text-anchor="middle" fill="#353537" class="sensitive" font-size="12">${{ fmtInt(p.value) }}</text>
                </template>
              </svg>
              <div v-if="countyRate" class="mt-1">County rate in {{ data.year }}: ${{ fmtInt(countyRate) }} per patient.</div>
            </div>
          </div>
        </section>

        <section class="rounded-lg border border-gray-900 p-4">
          <h3 class="heading-subsection mb-3">What to say</h3>
          <p class="sensitive text-sm text-gray-800" data-demo-blur>{{ brief.what_to_say }}</p>
        </section>
      </div>
    </div>

    <details class="mt-4 text-sm">
      <summary class="cursor-pointer text-gray-600 hover:text-gray-900 py-1">The numbers behind this brief</summary>
      <div class="mt-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table class="w-full text-xs">
          <tbody class="divide-y divide-gray-100">
            <tr v-for="(f, i) in data.facts" :key="i">
              <td class="py-1.5 px-3 text-gray-400 tabular-nums w-10 align-top">{{ i + 1 }}</td>
              <td class="py-1.5 px-3 text-gray-800 align-top font-mono break-all sensitive">{{ f.replace(/^\[\d+\] /, '') }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="text-[11px] text-gray-400 mt-2">Written by {{ data.model }} from graph build {{ (data.build_id || '').slice(0, 8) }}.</div>
    </details>
    </template>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import PracticeTag from './PracticeTag.vue'
import MetricCard from './tabs/MetricCard.vue'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import { exportBlockedInDemoMode } from '@/utils/demoSpoof'

const TAG_LABEL = {
  fqhc: 'FQHC or RHC',
  rural: 'Rural',
  health_system: 'Health system',
  specialty_primary_care: 'Specialty primary care',
  nursing_home_attending: 'Nursing home attending',
  hospitalist: 'Hospitalist',
  solo_provider: 'Solo',
  multi_provider: 'Group',
  large_panel: 'Large panel',
  small_panel: 'Small panel',
}

const VERDICT = {
  fixable: { label: 'Yes. This practice is fixable.', box: 'bg-secondary-100 border-secondary-300 text-secondary-1000' },
  partly: { label: 'Partly. Some of what holds it back is structural.', box: 'bg-yellow-light border-yellow-dark text-gray-900' },
  stuck: { label: 'Stuck. What holds it back is not something a program fixes.', box: 'bg-gray-100 border-gray-300 text-gray-900' },
}

export default {
  name: 'BriefTab',
  components: { FontAwesomeIcon, PracticeTag, MetricCard, ExportMenu },
  props: {
    practiceKey: { type: String, default: null },
    tagLabels: { type: Object, default: () => ({}) },
  },
  emits: ['back'],
  data() {
    return { data: null, loading: false, copied: false, exporting: null }
  },
  computed: {
    brief() {
      return this.data.brief
    },
    years() {
      return (this.data && this.data.years) || []
    },
    place() {
      return [this.data.county, this.data.state].filter(Boolean).join(', ')
    },
    writtenOn() {
      if (!this.data.updated_at) return ''
      return new Date(this.data.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    },
    shortName() {
      return (this.data.name || '').split(' ')[0]
    },
    structuralTagLabels() {
      return (this.data.structural_tags || []).map((t) => TAG_LABEL[t] || t)
    },
    findingTags() {
      return (this.data.tags || []).map((t) => this.tagLabels[t] || t)
    },
    owner() {
      const o = (this.data.owners || [])[0]
      return o ? o.ORGANIZATION_NAME : null
    },
    cohortLabel() {
      return (this.data.cohort_key || '').split('|').join(', ')
    },
    upsideMid() {
      return ((this.brief.peers.upside_low || 0) + (this.brief.peers.upside_high || 0)) / 2
    },
    verdictLabel() {
      return (VERDICT[this.brief.can_improve.verdict] || VERDICT.partly).label
    },
    verdictBox() {
      return (VERDICT[this.brief.can_improve.verdict] || VERDICT.partly).box
    },
    colWidth() {
      return 66
    },
    chartWidth() {
      return 20 + this.years.length * 66
    },
    maxLives() {
      return Math.max(...this.years.map((y) => y.LIVES || 0), 1)
    },
    breakIndex() {
      const idx = this.years.findIndex((y) => y.PANEL_BREAK)
      return idx >= 0 ? idx - 1 : -1
    },
    breakX() {
      return 10 + (this.breakIndex + 1) * this.colWidth - 11
    },
    pmpySeries() {
      return this.years
        .map((y, index) => ({ year: y.YEAR, index, value: y.RISK_ADJUSTED_PMPY }))
        .filter((p) => p.value != null)
    },
    pmpyPoints() {
      return this.pmpySeries.map((p) => `${10 + p.index * this.colWidth + (this.colWidth - 22) / 2},${this.pmpyY(p.value)}`).join(' ')
    },
    programRows() {
      return [...((this.data && this.data.programs) || [])].sort((a, b) => b.YEAR - a.YEAR)
    },
    acoResultRows() {
      const rows = (this.data && this.data.aco_results) || []
      const current = this.programRows[0] ? this.programRows[0].PROGRAM_ID : null
      return rows.filter((r) => !current || r.PROGRAM_ID === current).sort((a, b) => b.YEAR - a.YEAR)
    },
    acoStatusLabel() {
      const status = this.brief.aco.status
      if (status === 'current') return 'In an ACO today.'
      if (status === 'former') return 'Was in an ACO.'
      return 'Never in an ACO.'
    },
    countyRate() {
      const last = this.years[this.years.length - 1]
      return last ? last.COUNTY_PMPY : null
    },
  },
  watch: {
    practiceKey() {
      this.load()
    },
  },
  created() {
    this.load()
  },
  methods: {
    async load() {
      if (!this.practiceKey) return
      this.loading = true
      try {
        const { data } = await this.$axios.get(`/api/growth/briefs/${encodeURIComponent(this.practiceKey)}/`)
        this.data = data && data.practice_key ? data : null
      } finally {
        this.loading = false
      }
    },
    barHeight(v) {
      return Math.round(((v || 0) / this.maxLives) * 93)
    },
    pmpyY(v) {
      const vals = this.pmpySeries.map((p) => p.value)
      const min = Math.min(...vals)
      const max = Math.max(...vals)
      if (max === min) return 56
      return Math.round(56 - ((v - min) / (max - min)) * 40)
    },
    cite(facts) {
      return facts && facts.length ? '[' + facts.join(', ') + ']' : ''
    },
    fmtInt(v) {
      return v == null ? '' : Math.round(v).toLocaleString()
    },
    fmtPct(v) {
      return v == null ? '' : (v * 100).toFixed(1) + '%'
    },
    fmtMoney(v) {
      if (v == null) return ''
      if (v <= -1000000) return '-$' + (-v / 1000000).toFixed(1) + 'M'
      if (v < 0) return '-$' + Math.round(-v / 1000) + 'K'
      if (v >= 1000000) return '$' + (v / 1000000).toFixed(1) + 'M'
      if (v >= 1000) return '$' + Math.round(v / 1000) + 'K'
      return '$' + Math.round(v)
    },
    async copyLink() {
      const url = new URL(window.location.href)
      url.searchParams.set('practice', this.practiceKey)
      await navigator.clipboard.writeText(url.toString())
      this.copied = true
      setTimeout(() => { this.copied = false }, 2000)
    },
    async exportBrief(format) {
      if (exportBlockedInDemoMode()) return
      this.exporting = format
      try {
        const response = await this.$axios.get(`/api/growth/briefs/${encodeURIComponent(this.practiceKey)}/export/`, {
          params: { as: format },
          responseType: 'blob',
        })
        const url = URL.createObjectURL(response.data)
        const a = document.createElement('a')
        a.href = url
        a.download = `brief_${(this.data.name || this.practiceKey).replace(/[^A-Za-z0-9]+/g, '_')}.${format}`
        a.click()
        URL.revokeObjectURL(url)
      } finally {
        this.exporting = null
      }
    },
  },
}
</script>
